// MAJLIS-71: the flag-gated microphone diagnostics panel for the owner's phone test (UI-C19, UI-C10, EM-12; a T- row
// only, no mock-up frame: consultant ruling Q5, 2026-09-24). The recorder is media/micDiag.ts; this file is the flag,
// the wiring and the small overlay.
//
// THE FLAG. It is read ONCE, at page load, from the page URL's QUERY STRING: `?micdiag=1`, exactly. The app's routes
// live in the fragment, so the owner opens   https://<host>/?micdiag=1#/r/CODE   (the query BEFORE the `#`). The app
// keeps `location.search` when it rewrites the fragment (main.tsx setHash), so the address bar still carries it after a
// join and a reload keeps the panel.
// It never reaches a server request of the APP: the room socket's URL is built from protocol and host only
// (protocol/client.ts wsUrl), every API call uses a fixed path, and LiveKit is given its grant's URL; nothing here is
// handed to any of them. The one request that does carry it is the browser's own GET of the page, which the edge proxy
// serves as a static file and whose request URI it deletes from its logs (deploy/caddy/Caddyfile, NF-4).
//
// WITHOUT THE FLAG THIS DOES NOTHING: installMicDiag returns the very opener it was given and no room observer, adds no
// element, attaches no listener and starts no timer (micdiag.test.tsx holds both sides of that with spies). With it, it
// only observes (media/micDiag.ts), and the panel is its own React root beside #root, so the app's own DOM is unchanged
// either way.
import { useState, useSyncExternalStore, type CSSProperties } from "react";
import { createRoot } from "react-dom/client";
import type { Clock } from "./clock";
import { MICDIAG_LEVEL_MS, MICDIAG_MAX_LINES, MICDIAG_SAMPLE_MS, MicDiag, type WatchedContext } from "./media/micDiag";
import type { GumObserver, MicOpener } from "./media/mic";
import type { RoomObserver } from "./media/livekitRoom";
import type { SocketObserver } from "./protocol/client";

/** `?micdiag=1` in the page's query string, and nothing else, turns the panel on. */
export function micDiagFlag(search: string): boolean {
  return new URLSearchParams(search).get("micdiag") === "1";
}

/** MAJLIS-116 (T-147, DIAGNOSTIC ONLY, no product default changes). `?micdiag=1&ec=0` (exactly "0") requests
 * echoCancellation OFF for that session - to test the owner's report that after a pause his level reads low then
 * climbs (iOS voice processing, tied to echoCancellation, ramping gain). It parses ONLY TOGETHER WITH micdiag=1: a
 * stray `ec=0` with no `micdiag=1` is ignored, the same "the product never changes because of a stray query param"
 * rule as the flag above. Read ONCE, at page load, exactly like micDiagFlag, and for the same reason it never
 * reaches a server request: it is read from `location.search` here and handed straight to mic.ts's
 * `microphoneOpener`, never to the room socket's URL, an API call or LiveKit's grant (see the file header). */
export function micDiagEcOff(search: string): boolean {
  return micDiagFlag(search) && new URLSearchParams(search).get("ec") === "0";
}

export interface MicDiagEnv {
  /** window.location.search, read once at page load. */
  search: string;
  document: Document;
  window: Window;
  clock: Clock;
  /** The app's opener as it is without the flag (mic.ts openMicrophone). */
  openMic: MicOpener;
  /** mic.ts microphoneOpener: the same opener, with a getUserMedia observer and (MAJLIS-116, T-147, DIAGNOSTIC ONLY)
   * the echoCancellation choice `?ec=0` makes. Called only with the flag. */
  observedOpener: (observe: GumObserver, echoCancellation?: boolean) => MicOpener;
  /** mic.ts micAudioContext: the mic ring's AudioContext, read only. */
  audioContext: () => WatchedContext | null;
  /** Copy text to the clipboard (navigator.clipboard.writeText); a test injects its own. */
  copy: (text: string) => Promise<void>;
}

export interface MicDiagWiring {
  openMic: MicOpener;
  roomObserver?: RoomObserver;
  diag?: MicDiag;
  /** MAJLIS-116 (T-141): told once for each speech_start for THIS seat. App.tsx is the caller - it already knows
   * its own seat id (the same check it runs for seat_updated) and calls this only when a speech_start's seat matches
   * it; micDiag itself is handed no seat id to filter with. Undefined without the flag, the same pattern as
   * roomObserver. */
  speechStart?: () => void;
  /** MAJLIS-116 (T-141): the room socket's own close (protocol/client.ts's RoomClientOptions.observer), given only
   * with the flag - the same pattern as roomObserver. */
  socketObserver?: SocketObserver;
}

export function installMicDiag(env: MicDiagEnv): MicDiagWiring {
  if (!micDiagFlag(env.search)) return { openMic: env.openMic };

  const { clock } = env;
  const ecOff = micDiagEcOff(env.search); // MAJLIS-116 (T-147, DIAGNOSTIC ONLY): see micDiagEcOff above
  const diag = new MicDiag(() => clock.now());
  diag.record("micdiag on", { sampleMs: MICDIAG_SAMPLE_MS, maxLines: MICDIAG_MAX_LINES });
  diag.watchPage(env.document, env.window);
  clock.setInterval(() => diag.sample(env.audioContext()), MICDIAG_SAMPLE_MS);
  // MAJLIS-116 (T-141): once a second, whether or not the microphone is open yet (sampleLevel is a no-op until
  // watchLevel below gives it a source).
  clock.setInterval(() => diag.sampleLevel(), MICDIAG_LEVEL_MS);

  const gum: GumObserver = {
    call: () => diag.record("gum call"),
    result: (r) => ("error" in r ? diag.record("gum error", { error: r.error }) : diag.record("gum ok", { tracks: r.tracks })),
    // MAJLIS-98: each resume() of the ring's AudioContext - its trigger and the error NAME ("none": it resolved)
    resume: (r) => diag.record("audioctx resume", { trigger: r.trigger, error: r.error }),
  };
  const opener = env.observedOpener(gum, !ecOff);
  const openMic: MicOpener = async () => {
    // MAJLIS-116 (T-147, DIAGNOSTIC ONLY): which constraint set THIS open requests - not what Safari applied (that
    // is the "mic settings" line below, read from getSettings() after the fact).
    diag.record("mic requested", { ec: ecOff ? "off" : "on" });
    const r = await opener();
    diag.record("mic opened", { permission: r.permission });
    diag.watchTrack(r.source?.track ?? null);
    diag.watchContext(env.audioContext());
    diag.watchLevel(r.source); // MAJLIS-116 (T-141): the same MicSource, never a second analyser
    return r;
  };
  const roomObserver: RoomObserver = {
    connectionState: (state) => diag.record("lk connection", { state }),
    localTrack: (event) => diag.record(`lk local track ${event}`),
    // MAJLIS-116 (T-141): reason is already one of livekitRoom.ts's fixed names, "other" or null - passed straight on
    disconnected: (reason, visibility) => diag.record("lk disconnected", { reason, visibility }),
  };
  const socketObserver: SocketObserver = {
    close: (code, clean) => diag.record("socket close", { code, clean }),
  };

  const host = env.document.createElement("div");
  host.id = "micdiag";
  env.document.body.appendChild(host);
  createRoot(host).render(<MicDiagPanel diag={diag} copy={env.copy} />);
  return { openMic, roomObserver, diag, speechStart: () => diag.speechStart(), socketObserver };
}

const PANEL: CSSProperties = {
  position: "fixed",
  top: 0,
  insetInline: 0,
  zIndex: 1000,
  maxHeight: "40vh",
  display: "flex",
  flexDirection: "column",
  background: "var(--surface)",
  color: "var(--ink)",
  borderBottom: "1px solid var(--line)",
  fontSize: "12px",
  direction: "ltr",
};
const BAR: CSSProperties = { display: "flex", gap: "8px", alignItems: "center", padding: "4px 8px" };
const LOG: CSSProperties = { margin: 0, padding: "0 8px 8px", overflow: "auto", fontFamily: "monospace", fontSize: "12px" };

/** The overlay: the log, newest last; "Copy log"; "Hide" folds it to its bar. Text only, nothing else it can do. */
export function MicDiagPanel({ diag, copy }: { diag: MicDiag; copy: (text: string) => Promise<void> }) {
  useSyncExternalStore(diag.subscribe, diag.version);
  const [open, setOpen] = useState(true);
  const [status, setStatus] = useState<"" | "copied" | "failed">("");
  const [manual, setManual] = useState<string | null>(null);
  const lines = diag.lines();
  const onCopy = () => {
    const text = diag.text();
    copy(text).then(
      () => {
        setStatus("copied");
        setManual(null);
      },
      () => {
        // no clipboard (an old browser, a refused permission): show the text selected, to copy by hand
        setStatus("failed");
        setManual(text);
      },
    );
  };
  return (
    <div data-micdiag="panel" style={PANEL}>
      <div style={BAR}>
        <strong>micdiag</strong>
        <span data-micdiag="count">{lines.length} lines</span>
        <button type="button" data-micdiag="copy" onClick={onCopy}>
          Copy log
        </button>
        <button type="button" data-micdiag="toggle" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"}
        </button>
        {status === "copied" && <span data-micdiag="status">copied</span>}
        {status === "failed" && <span data-micdiag="status">copy failed: select the text below</span>}
      </div>
      {manual !== null && (
        <textarea
          data-micdiag="manual"
          readOnly
          value={manual}
          rows={4}
          style={{ margin: "0 8px 8px", fontSize: "12px" }}
          ref={(el) => el?.select()}
        />
      )}
      {open && <pre data-micdiag="log" style={LOG}>{lines.join("\n")}</pre>}
    </div>
  );
}
